/* eslint-disable  */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
    processQuestions,
    renderQuestion,
    transformFormData,
    createInitialFormValues
} from './FunctionsForm'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { ContainerGroupField, TimerTips } from '@/components/common'
import { FormNavigation } from '@/components/form-controls'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { FormsGroupsProps } from '@/types/forms/formStatus'
import { useFormProgress } from '@/utils'

interface FormResponse {
    [key: string]: string | string[] // Allow both single values and arrays
}

// API response type for questions
interface ApiQuestionData {
    id: number
    name?: string
    label: string
    type: '1' | '2' | '3'
    options: {
        id: number
        value: string
        label: string
    }[]
    order: number
    pickedOptions: string[] | null
    responseSecondaryExplication?: {
        explanationOptionIds?: number[]
    } | null
    requiredExplication: {
        required: boolean
        valueFlag: string[]
        options?: {
            id: number
            label: string
            value: string
        }[]
    }
    responseText: string | null
}

interface ProcessedQuestion extends ApiQuestionData {
    fieldName: string
    hasExplanation: boolean
    explanationOptions: Array<{
        id: number
        label: string
        value: string
    }> | null
    selectedValues: string[]
    selectedExplanationValues: string[]
}

interface AdmissionTestFormProps extends FormsGroupsProps {
    onTotalPages: (length: number) => void
    currentPage: number
    onPageChange: (page: number) => void
}

export const AdmissionTestForm = ({
    onNext,
    onPrevious,
    onTotalPages,
    currentPage,
    onPageChange
}: AdmissionTestFormProps): React.ReactElement => {
    const { t } = useTranslation()
    const { control, handleSubmit, watch, setValue, reset, getValues } =
        useForm<FormResponse>({
            defaultValues: {},
            mode: 'onChange',
            shouldUnregister: false
        })

    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const { idAspirant, idInscription } = useAuthHook()
    const { showLoader, hideLoader } = useProviderLoader()
    const [questions, setQuestions] = useState<ProcessedQuestion[]>([])
    const [isReadOnly, setIsReadOnly] = useState<boolean>(false)

    // Custom progress calculation for questions
    const calculateQuestionProgress = (): number => {
        if (questions.length === 0) return 0

        const formValues = watch()
        let answeredQuestions = 0

        questions.forEach((question) => {
            const fieldValue = formValues[question.fieldName]

            // Check if the question has been answered
            if (fieldValue) {
                if (Array.isArray(fieldValue)) {
                    // For checkbox questions (type 2)
                    if (fieldValue.length > 0) {
                        answeredQuestions++
                    }
                } else {
                    // For radio/select questions (type 1/3)
                    if (
                        fieldValue !== '' &&
                        fieldValue !== null &&
                        fieldValue !== undefined
                    ) {
                        answeredQuestions++
                    }
                }
            }
        })

        return Math.round((answeredQuestions / questions.length) * 100)
    }

    const progress = calculateQuestionProgress()

    // Load test data
    useEffect(() => {
        const loadTestData = async (): Promise<void> => {
            if (!idAspirant) return

            showLoader()

            try {
                const questionsResult =
                    await aspirantsApi.getQuestionsByFormId(9)

                if (questionsResult.status === 200) {
                    const processedQuestions = processQuestions(
                        questionsResult.data
                    )
                    setQuestions(processedQuestions)
                    onTotalPages(processedQuestions.length)

                    // Only initialize form values if we don't have any current values
                    const currentValues = getValues()
                    const hasCurrentValues = Object.keys(currentValues).some(
                        (key) => {
                            const value = currentValues[key]
                            return (
                                value !== '' &&
                                value !== undefined &&
                                value !== null &&
                                (!Array.isArray(value) || value.length > 0)
                            )
                        }
                    )

                    if (!hasCurrentValues) {
                        const initialValues =
                            createInitialFormValues(processedQuestions)
                        reset(initialValues)
                    }
                }
            } catch (error) {
                console.error('Error loading test data:', error)
            } finally {
                hideLoader()
            }
        }

        loadTestData()
    }, [idAspirant, reset, getValues])

    const onSubmit = async (data: FormResponse): Promise<void> => {
        if (currentPage < questions.length) {
            // Force the form to keep current values when moving to next page
            const currentFormData = getValues()

            let nextPage = currentPage + 1
            const currentQuestion = questions[currentPage - 1]
            const currentValue = data[currentQuestion.fieldName]

            // Logic to skip questions based on specific rules
            // If Question ID 3 (Accompaniment) is 'No', skip to Question ID 5 (Health)
            if (currentQuestion.id === 3 && currentValue === 'No') {
                const targetIndex = questions.findIndex((q) => q.id === 5)
                if (targetIndex !== -1) {
                    nextPage = targetIndex + 1
                }
            }

            onPageChange(nextPage)
            // Small delay to ensure page change, then restore values if needed
            setTimeout(() => {
                Object.keys(currentFormData).forEach((key) => {
                    const currentValue = getValues(key)
                    if (
                        !currentValue ||
                        (Array.isArray(currentValue) &&
                            currentValue.length === 0)
                    ) {
                        setValue(key, currentFormData[key])
                    }
                })
            }, 10)
            return
        }

        if (!idAspirant) return

        showLoader()

        try {
            const formattedResponses = transformFormData(data, questions)
            const responsesArray = Object.values(formattedResponses)
            const idForm = 9
            console.log('idInscription', idInscription)
            const response = await aspirantsApi.postPsychosocialData(
                idAspirant,
                idForm,
                idInscription!,
                responsesArray
            )

            if (response.status === 200) {
                // Add a small delay to allow backend processing to start
                console.log(
                    'Test submitted successfully, allowing backend processing time...'
                )
                setTimeout(() => {
                    onNext()
                }, 1000) // 1 second delay
            }
        } catch (error) {
            console.error('Error submitting test:', error)
        } finally {
            hideLoader()
        }
    }

    const changeBeforePage = (): void => {
        // Preserve current form state when going back
        const currentFormData = getValues()

        let prevPage = currentPage - 1
        const currentQuestion = questions[currentPage - 1]

        // Logic to handle backward navigation for skipped questions
        // If we are at Question ID 5 (Health) and ID 3 was 'No', go back to ID 3
        if (currentQuestion.id === 5) {
            const q3 = questions.find((q) => q.id === 3)
            if (q3) {
                const val3 = getValues(q3.fieldName)
                if (val3 === 'No') {
                    const targetIndex = questions.findIndex((q) => q.id === 3)
                    if (targetIndex !== -1) {
                        prevPage = targetIndex + 1
                    }
                }
            }
        }

        onPageChange(prevPage)
        // Small delay to ensure page change, then restore values if needed
        setTimeout(() => {
            Object.keys(currentFormData).forEach((key) => {
                const currentValue = getValues(key)
                if (
                    !currentValue ||
                    (Array.isArray(currentValue) && currentValue.length === 0)
                ) {
                    setValue(key, currentFormData[key])
                }
            })
        }, 10)
    }

    // Get current question
    const currentQuestion = questions[currentPage - 1]

    if (isReadOnly) {
        return (
            <div className="w-full space-y-4">
                <TimerTips
                    time=""
                    title={t('tips.testCompleted')}
                />
                <ContainerGroupField
                    tittleGroup={t('forms.groups.AdmissionTest')}
                    onlyOneField
                >
                    <div className="text-center p-8">
                        <h3 className="text-lg font-semibold mb-4">
                            {t('AdmissionTest.alreadyCompleted')}
                        </h3>
                        <p className="text-gray-600">
                            {t('AdmissionTest.canOnlyTakeOnce')}
                        </p>
                    </div>
                </ContainerGroupField>
                <FormNavigation
                    onSubmit={() => onNext()}
                    onPrevious={onPrevious}
                    progress={100}
                    submitLabel={t('forms.ui.next')}
                />
            </div>
        )
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full space-y-4"
        >
            <TimerTips
                time={t('tips.timer', { time: '5' })}
                title={t('tips.test')}
            />

            <ContainerGroupField
                tittleGroup={t('forms.groups.AdmissionTest')}
                onlyOneField
            >
                {currentQuestion &&
                    renderQuestion({
                        question: currentQuestion,
                        control,
                        watch,
                        setValue,
                        requiredText: t('AdmissionTest.required'),
                        description: t('AdmissionTest.description'),
                        isReadOnly
                    })}
            </ContainerGroupField>

            <FormNavigation
                onSubmit={handleSubmit(onSubmit)}
                onPrevious={currentPage === 1 ? onPrevious : changeBeforePage}
                progress={progress}
                submitLabel={
                    currentPage === questions.length
                        ? t('forms.ui.next')
                        : t('forms.ui.nextPage')
                }
            />
        </form>
    )
}
